import React, { memo, useCallback, useMemo, useState } from 'react';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Sheet, SheetContent, SheetTrigger } from 'components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Card, CardContent } from 'components/ui/card';
import { IconSearch } from '@app/icons';
import {
  Kato,
  Oked,
  Tender,
  TenderTypes,
  Vehicle,
  VehicleStatus,
  VehicleStatusNames,
} from '@app/models';
import { RenderFilterProps } from '@app/templates';
import { BrandSelect } from '@app/components';
import { useForm } from '../hooks';
import TendersFilter from '../TendersFilter';
import { VehicleTypeSelect } from './components';
import { KatoSelectTrigger } from '@app/components';
import { OkedSelectTrigger } from '@app/components';
import { SelectOption } from '@app/components';

interface FormData {
  kato: SelectOption<Kato> | null;
  brand: string;
  type: string | null;
  status: SelectOption<VehicleStatus> | null;
  oked: SelectOption<Oked> | null;
}

export type VehiclesFilterData<T> = {
  tenderType: TenderTypes;
} & Partial<T>;

interface Props
  extends RenderFilterProps<VehiclesFilterData<Vehicle | Tender>> {}

function VehiclesFilter(props: Props) {
  const { onSubmit, pending, okedParentNumber } = props;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [tenderType, setTenderType] = useState<TenderTypes>(
    TenderTypes.REQUEST
  );
  const { values, onChange, setValues } = useForm<FormData>({
    brand: '',
    kato: null,
    type: null,
    status: null,
    oked: null,
  });

  const onChangeTenderType = useCallback(
    (item: TenderTypes) => () => {
      setTenderType(item);

      onSubmit({
        tenderType: item,
      });
    },
    [onSubmit]
  );

  const onChangeBrand = useCallback(
    (value: string) => {
      onChange(value, 'brand');
    },
    [onChange]
  );

  const onClickSearch = useCallback(() => {
    const params: VehiclesFilterData<Vehicle> = {
      tenderType,
    };

    if (!!values.kato) {
      params.katoCode = values.kato.value.code;
    }

    if (!!values.brand) {
      params.brand = values.brand;
    }

    if (!!values.type) {
      params.type = values.type;
    }

    if (!!values.status) {
      params.status = values.status.value;
    }

    if (!!values.oked) {
      params.okedCode = values.oked.value.code
        ? values.oked.value.code
        : String(values.oked.value.number);
    }

    onSubmit(params);
  }, [onSubmit, values, tenderType]);

  const onClickClear = useCallback(() => {
    setValues({
      brand: '',
      kato: null,
      type: null,
      status: null,
      oked: null,
    });

    onSubmit({
      tenderType,
    });
  }, [onSubmit, setValues, tenderType]);

  const onSubmitTendersFilter = useCallback(
    (tenderFilterData: Partial<Tender>) => {
      onSubmit({
        tenderType,
        ...tenderFilterData,
      });
    },
    [onSubmit, tenderType]
  );

  const vehicleStatusOptions = useMemo<SelectOption<VehicleStatus>[]>(
    () =>
      [VehicleStatus.FREE, VehicleStatus.BUSY].map((status) => ({
        label: VehicleStatusNames[status],
        value: status,
      })),
    []
  );

  const onClickClearKato = useCallback(() => {
    setValues({
      kato: null,
    });
  }, [setValues]);

  const onClickClearOked = useCallback(() => {
    setValues({
      oked: null,
    });
  }, [setValues]);

  return (
    <Card className="w-full border-0 shadow-none">
      <CardContent className="p-0">
        <div className="mb-6">
          <RadioGroup
            value={tenderType.toString()}
            onValueChange={(value) =>
              onChangeTenderType(parseInt(value) as TenderTypes)()
            }
            className="flex flex-col gap-4 sm:flex-row sm:gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value={TenderTypes.REQUEST.toString()}
                id="rent-out"
              />
              <Label
                htmlFor="rent-out"
                className="text-sm font-medium cursor-pointer"
              >
                Сдать в аренду
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value={TenderTypes.OFFER.toString()}
                id="rent-in"
              />
              <Label
                htmlFor="rent-in"
                className="text-sm font-medium cursor-pointer"
              >
                Арендовать спецтехнику
              </Label>
            </div>
          </RadioGroup>
        </div>

        {tenderType === TenderTypes.REQUEST && (
          <TendersFilter
            pending={pending}
            onSubmit={onSubmitTendersFilter}
            okedParentNumber={okedParentNumber}
          />
        )}

        {tenderType === TenderTypes.OFFER && (
          <>
            <div className="space-y-6 hidden md:block">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full">
                <div className="space-y-2">
                  <BrandSelect onChange={onChangeBrand} />
                </div>

                <div className="space-y-2">
                  <KatoSelectTrigger
                    label="РЕГИОН"
                    value={
                      (!!values.kato ? values.kato.value.nameRu : '') ||
                      undefined
                    }
                    placeholder="Выберите регион"
                    name="kato"
                    onChange={onChange}
                    onClickClear={onClickClearKato}
                  />
                </div>

                <div className="space-y-2">
                  <VehicleTypeSelect
                    value={values.type}
                    onChange={onChange}
                    name="type"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status" className="text-sm font-medium">
                    СТАТУС
                  </Label>
                  <Select
                    value={values.status?.value?.toString() || ''}
                    onValueChange={(value: string) => {
                      const selectedOption = vehicleStatusOptions.find(
                        (opt) => opt.value.toString() === value
                      );
                      onChange(selectedOption || null, 'status');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Все" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleStatusOptions.map((option) => (
                        <SelectItem
                          key={option.value.toString()}
                          value={option.value.toString()}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                  <OkedSelectTrigger
                    label="КАТЕГОРИЯ"
                    name="oked"
                    value={!!values.oked ? values.oked.value.nameRu : ''}
                    placeholder="Выберите категорию"
                    onChange={onChange}
                    parentNumber={okedParentNumber}
                    onClickClear={onClickClearOked}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 pt-4 border-t w-full sm:flex-row sm:justify-between">
                <Button
                  variant="outline"
                  onClick={onClickClear}
                  className="reset-btn w-full sm:w-auto"
                >
                  Сбросить все
                </Button>

                <Button
                  disabled={pending}
                  onClick={onClickSearch}
                  className="w-full sm:w-auto min-w-[120px]"
                >
                  {pending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  ) : (
                    <IconSearch className="h-4 w-4 mr-2" />
                  )}
                  Найти
                </Button>
              </div>
            </div>

            <div className="md:hidden">
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <IconSearch className="h-4 w-4 mr-2" />
                    Расширенный поиск
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[80vh]">
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <BrandSelect onChange={onChangeBrand} />
                      </div>

                      <div className="space-y-2">
                        <KatoSelectTrigger
                          label="РЕГИОН"
                          value={
                            (!!values.kato ? values.kato.value.nameRu : '') ||
                            undefined
                          }
                          placeholder="Выберите регион"
                          name="kato"
                          onChange={onChange}
                          onClickClear={onClickClearKato}
                        />
                      </div>

                      <div className="space-y-2">
                        <VehicleTypeSelect
                          value={values.type}
                          onChange={onChange}
                          name="type"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="status-mobile"
                          className="text-sm font-medium"
                        >
                          СТАТУС
                        </Label>
                        <Select
                          value={values.status?.value?.toString() || ''}
                          onValueChange={(value: string) => {
                            const selectedOption = vehicleStatusOptions.find(
                              (opt) => opt.value.toString() === value
                            );
                            onChange(selectedOption || null, 'status');
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Все" />
                          </SelectTrigger>
                          <SelectContent>
                            {vehicleStatusOptions.map((option) => (
                              <SelectItem
                                key={option.value.toString()}
                                value={option.value.toString()}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <OkedSelectTrigger
                          label="КАТЕГОРИЯ"
                          name="oked"
                          value={!!values.oked ? values.oked.value.nameRu : ''}
                          placeholder="Выберите категорию"
                          onChange={onChange}
                          parentNumber={okedParentNumber}
                          onClickClear={onClickClearOked}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 pt-4 border-t w-full">
                      <Button
                        variant="outline"
                        onClick={onClickClear}
                        className="w-full"
                      >
                        Сбросить все
                      </Button>

                      <Button
                        disabled={pending}
                        onClick={() => {
                          onClickSearch();
                          setIsSheetOpen(false);
                        }}
                        className="w-full"
                      >
                        {pending ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                        ) : (
                          <IconSearch className="h-4 w-4 mr-2" />
                        )}
                        Найти
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default memo(VehiclesFilter);
